import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Tab,
  Tabs,
  Paper,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Divider,
  Alert,
  IconButton
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SitePreview, { getSalesSiteApiBase } from './SitePreview';
import SaveIcon from '@mui/icons-material/Save';
import PaletteIcon from '@mui/icons-material/Palette';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';

const SalesSiteDesign = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [primaryDomain, setPrimaryDomain] = useState(null);

  useEffect(() => {
    loadSettings();
    loadPrimaryDomain();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = getSalesSiteApiBase();
      
      const response = await axios.get(`${API_URL}sales-site/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings(response.data.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      enqueueSnackbar('Ошибка загрузки настроек', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadPrimaryDomain = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = getSalesSiteApiBase();
      
      const response = await axios.get(`${API_URL}sales-site/domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Находим основной домен
      const mainDomain = response.data.data.find(d => d.type === 'main');
      if (mainDomain) {
        setPrimaryDomain(mainDomain.domain);
      }
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = getSalesSiteApiBase();
      
      await axios.put(`${API_URL}sales-site/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Обновляем preview
      setPreviewKey(prev => prev + 1);
      
      enqueueSnackbar('Настройки успешно сохранены', { variant: 'success' });
    } catch (error) {
      console.error('Error saving settings:', error);
      enqueueSnackbar('Ошибка сохранения настроек', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Сбросить все настройки к значениям по умолчанию?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = getSalesSiteApiBase();
      
      const response = await axios.post(`${API_URL}sales-site/settings/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings(response.data.data);
      enqueueSnackbar('Настройки сброшены', { variant: 'success' });
    } catch (error) {
      console.error('Error resetting settings:', error);
      enqueueSnackbar('Ошибка сброса настроек', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const API_URL = getSalesSiteApiBase();
        
        const response = await axios.post(`${API_URL}sales-site/settings/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        // Обновляем URL в настройках
        if (type === 'logo') {
          setSettings(prev => ({ ...prev, logo: response.data.url }));
        } else if (type === 'favicon') {
          setSettings(prev => ({ ...prev, favicon: response.data.url }));
        } else if (type === 'background') {
          setSettings(prev => ({
            ...prev,
            hero: {
              ...prev.hero,
              background: {
                ...prev.hero.background,
                image: response.data.url
              }
            }
          }));
        }

        enqueueSnackbar('Файл успешно загружен', { variant: 'success' });
      } catch (error) {
        console.error('Error uploading file:', error);
        enqueueSnackbar('Ошибка загрузки файла', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    input.click();
  };

  const updateSettings = (path, value) => {
    setSettings(prev => {
      const keys = path.split('.');
      const newSettings = { ...prev };
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  if (!settings) {
    return <Box sx={{ p: 3 }}><Typography>Загрузка...</Typography></Box>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PaletteIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Дизайн сайта
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PaletteIcon />}
            onClick={() => navigate('/sales-site/visual-editor')}
          >
            Визуальный редактор
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={loading}
          >
            Сбросить
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Сохранить
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Левая колонка - Настройки */}
        <Grid item xs={12} lg={7}>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Общие" />
          <Tab label="Hero секция" />
          <Tab label="Стиль" />
          <Tab label="Карточки" />
          <Tab label="SEO" />
          <Tab label="Код" />
        </Tabs>
      </Paper>

      {/* Вкладка: Общие */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Основные настройки
                </Typography>
                
                <TextField
                  fullWidth
                  label="Название сайта"
                  value={settings.siteName}
                  onChange={(e) => updateSettings('siteName', e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Логотип
                </Typography>
                {settings.logo && (
                  <Box sx={{ mb: 1 }}>
                    <img src={settings.logo} alt="Logo" style={{ maxWidth: '200px', maxHeight: '100px' }} />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => handleFileUpload('logo')}
                  disabled={loading}
                  fullWidth
                >
                  Загрузить логотип
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Favicon
                </Typography>
                {settings.favicon && (
                  <Box sx={{ mb: 1 }}>
                    <img src={settings.favicon} alt="Favicon" style={{ maxWidth: '32px', maxHeight: '32px' }} />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => handleFileUpload('favicon')}
                  disabled={loading}
                  fullWidth
                >
                  Загрузить favicon
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Контакты
                </Typography>
                
                <TextField
                  fullWidth
                  label="Telegram"
                  value={settings.contacts.telegram}
                  onChange={(e) => updateSettings('contacts.telegram', e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  placeholder="@username"
                />

                <TextField
                  fullWidth
                  label="Email"
                  value={settings.contacts.email}
                  onChange={(e) => updateSettings('contacts.email', e.target.value)}
                  disabled={loading}
                  placeholder="support@example.com"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Вкладка: Hero секция */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.hero.enabled}
                  onChange={(e) => updateSettings('hero.enabled', e.target.checked)}
                  disabled={loading}
                />
              }
              label="Показывать Hero секцию"
              sx={{ mb: 3 }}
            />

            {settings.hero.enabled && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Заголовок"
                    value={settings.hero.title}
                    onChange={(e) => updateSettings('hero.title', e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Подзаголовок"
                    value={settings.hero.subtitle}
                    onChange={(e) => updateSettings('hero.subtitle', e.target.value)}
                    disabled={loading}
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Высота секции</InputLabel>
                    <Select
                      value={settings.hero.height}
                      onChange={(e) => updateSettings('hero.height', e.target.value)}
                      disabled={loading}
                      label="Высота секции"
                    >
                      <MenuItem value="small">Маленькая</MenuItem>
                      <MenuItem value="medium">Средняя</MenuItem>
                      <MenuItem value="large">Большая</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Фон
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Тип фона</InputLabel>
                    <Select
                      value={settings.hero.background.type}
                      onChange={(e) => updateSettings('hero.background.type', e.target.value)}
                      disabled={loading}
                      label="Тип фона"
                    >
                      <MenuItem value="color">Цвет</MenuItem>
                      <MenuItem value="gradient">Градиент</MenuItem>
                      <MenuItem value="image">Изображение</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {settings.hero.background.type === 'color' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Цвет фона"
                      type="color"
                      value={settings.hero.background.color}
                      onChange={(e) => updateSettings('hero.background.color', e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                )}

                {settings.hero.background.type === 'gradient' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Цвет 1"
                        type="color"
                        value={settings.hero.background.gradient.from}
                        onChange={(e) => updateSettings('hero.background.gradient.from', e.target.value)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Цвет 2"
                        type="color"
                        value={settings.hero.background.gradient.to}
                        onChange={(e) => updateSettings('hero.background.gradient.to', e.target.value)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Угол (градусы)"
                        type="number"
                        value={settings.hero.background.gradient.angle}
                        onChange={(e) => updateSettings('hero.background.gradient.angle', parseInt(e.target.value))}
                        disabled={loading}
                        inputProps={{ min: 0, max: 360 }}
                      />
                    </Grid>
                  </>
                )}

                {settings.hero.background.type === 'image' && (
                  <Grid item xs={12}>
                    {settings.hero.background.image && (
                      <Box sx={{ mb: 2 }}>
                        <img src={settings.hero.background.image} alt="Background" style={{ maxWidth: '100%', maxHeight: '200px' }} />
                      </Box>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => handleFileUpload('background')}
                      disabled={loading}
                      fullWidth
                    >
                      Загрузить изображение
                    </Button>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.hero.background.overlay.enabled}
                        onChange={(e) => updateSettings('hero.background.overlay.enabled', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Затемнение фона"
                  />
                </Grid>

                {settings.hero.background.overlay.enabled && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Цвет затемнения"
                        type="color"
                        value={settings.hero.background.overlay.color}
                        onChange={(e) => updateSettings('hero.background.overlay.color', e.target.value)}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom>Прозрачность: {settings.hero.background.overlay.opacity}%</Typography>
                      <Slider
                        value={settings.hero.background.overlay.opacity}
                        onChange={(e, v) => updateSettings('hero.background.overlay.opacity', v)}
                        disabled={loading}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Вкладка: Стиль */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Цветовая схема
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Основной цвет"
                  type="color"
                  value={settings.colors.primary}
                  onChange={(e) => updateSettings('colors.primary', e.target.value)}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Вторичный цвет"
                  type="color"
                  value={settings.colors.secondary}
                  onChange={(e) => updateSettings('colors.secondary', e.target.value)}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Цвет фона"
                  type="color"
                  value={settings.colors.background}
                  onChange={(e) => updateSettings('colors.background', e.target.value)}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Цвет текста"
                  type="color"
                  value={settings.colors.text}
                  onChange={(e) => updateSettings('colors.text', e.target.value)}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Фон карточек"
                  type="color"
                  value={settings.colors.cardBg}
                  onChange={(e) => updateSettings('colors.cardBg', e.target.value)}
                  disabled={loading}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 3 }}>
              Шрифты
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Шрифт заголовков</InputLabel>
                  <Select
                    value={settings.fonts.heading}
                    onChange={(e) => updateSettings('fonts.heading', e.target.value)}
                    disabled={loading}
                    label="Шрифт заголовков"
                  >
                    <MenuItem value="Roboto">Roboto</MenuItem>
                    <MenuItem value="Montserrat">Montserrat</MenuItem>
                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                    <MenuItem value="Lato">Lato</MenuItem>
                    <MenuItem value="Poppins">Poppins</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Шрифт текста</InputLabel>
                  <Select
                    value={settings.fonts.body}
                    onChange={(e) => updateSettings('fonts.body', e.target.value)}
                    disabled={loading}
                    label="Шрифт текста"
                  >
                    <MenuItem value="Roboto">Roboto</MenuItem>
                    <MenuItem value="Montserrat">Montserrat</MenuItem>
                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                    <MenuItem value="Lato">Lato</MenuItem>
                    <MenuItem value="Poppins">Poppins</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Вкладка: Карточки */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Настройки карточек товаров
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.productCards.showPhoto}
                  onChange={(e) => updateSettings('productCards.showPhoto', e.target.checked)}
                  disabled={loading}
                />
              }
              label="Показывать фото товара"
              sx={{ mb: 2, display: 'block' }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.productCards.showDescription}
                  onChange={(e) => updateSettings('productCards.showDescription', e.target.checked)}
                  disabled={loading}
                />
              }
              label="Показывать описание товара"
              sx={{ mb: 3, display: 'block' }}
            />

            <FormControl fullWidth>
              <InputLabel>Стиль карточек</InputLabel>
              <Select
                value={settings.productCards.style}
                onChange={(e) => updateSettings('productCards.style', e.target.value)}
                disabled={loading}
                label="Стиль карточек"
              >
                <MenuItem value="card">Карточка</MenuItem>
                <MenuItem value="minimal">Минимальный</MenuItem>
                <MenuItem value="detailed">Детальный</MenuItem>
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Вкладка: SEO */}
      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              SEO настройки
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={settings.seo.title}
                  onChange={(e) => updateSettings('seo.title', e.target.value)}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={settings.seo.description}
                  onChange={(e) => updateSettings('seo.description', e.target.value)}
                  disabled={loading}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Keywords (через запятую)"
                  value={settings.seo.keywords}
                  onChange={(e) => updateSettings('seo.keywords', e.target.value)}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Аналитика
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Google Analytics ID"
                  value={settings.analytics.googleAnalytics}
                  onChange={(e) => updateSettings('analytics.googleAnalytics', e.target.value)}
                  disabled={loading}
                  placeholder="G-XXXXXXXXXX"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Яндекс.Метрика ID"
                  value={settings.analytics.yandexMetrika}
                  onChange={(e) => updateSettings('analytics.yandexMetrika', e.target.value)}
                  disabled={loading}
                  placeholder="12345678"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Вкладка: Код */}
      {activeTab === 5 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Кастомный CSS
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Добавьте свой CSS код для дополнительной кастомизации сайта
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={15}
              value={settings.customCSS}
              onChange={(e) => updateSettings('customCSS', e.target.value)}
              disabled={loading}
              placeholder="/* Ваш CSS код */"
              sx={{ fontFamily: 'monospace', mb: 3 }}
            />
          </CardContent>
        </Card>
      )}

        </Grid>

        {/* Правая колонка - Live Preview */}
        <Grid item xs={12} lg={5}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    Live Preview
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => window.open(primaryDomain ? `https://${primaryDomain}` : 'http://localhost:8080', '_blank')}
                    title="Открыть в новой вкладке"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Box>
                {primaryDomain ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        Предпросмотр: <strong>{primaryDomain}</strong>
                      </Box>
                      <Box>
                        <Button
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={() => setPreviewKey(prev => prev + 1)}
                          sx={{ mr: 1 }}
                        >
                          Обновить
                        </Button>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => window.open(`https://${primaryDomain}`, '_blank')}
                        >
                          Открыть напрямую
                        </Button>
                      </Box>
                    </Alert>
                    <Box
                      sx={{
                        width: '100%',
                        height: '65vh',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        backgroundColor: '#fff'
                      }}
                    >
                      <SitePreview settings={settings} previewKey={previewKey} />
                    </Box>
                  </>
                ) : (
                  <Alert severity="warning">
                    Добавьте основной домен в разделе "Домены" чтобы увидеть предпросмотр сайта
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesSiteDesign;
